import { Reducer, useEffect, useMemo, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { ESF_STATIONLIST, NewInput, TooltipString, NewSelect, ToggleBox, EditSectionModal, EditTerminalsCard } from "../..";
import { currentEsfViewTypes, useEsfContext } from "../_EsfWalkThrough/EsfContext";
import { NewFieldStyles, NewFieldTypes, NewInputHandles, NewSelectHandles, SystemTypes, ToggleStyles } from "../../../Definitions";
import { DeviceInfo, Graph_QL, useDeviceInfo, useOldSection, useTerminals } from "../../../Services";
import { Location, Section } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";
import { selectStationByLocationId } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice";
import { RenameStation, UpdateTerminal } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/createLfSectionCall";
import { useDispatch } from "react-redux";
import { StoreAppDispatch, useAppSelector } from "../../../Store";
import { showSuccessWithTimeout } from "../../../Store/Thunks/successWithTimeout";
import { BaseIO } from "../../GlobalModals/editSectionModal";
import { TerminalDataProps, terminalsData } from "../../GlobalComponents/_GlobalComponents/editTerminalsData";
import LocationPopper from "../../GlobalComponents/SubComponents/editTerminalsDataPopover";
import { ITerminalResContext, TerminalResContext, TerminalResDispatcherT, TerminalResReducer } from "../../GlobalComponents/_GlobalComponents/terminalReducer";
import { LocationWithSectionAndSystem } from "../../../Services/Agrisys_GqlDataCaller/SectionsCalls/locationSlice";

interface NewSectionModal_Props {
  closeModal: () => void;
  location: LocationWithSectionAndSystem;
  section: Section;
  focus_view?: boolean;
}

export default function EditEsfWfSectionModal({ closeModal, section, location, focus_view = false }: NewSectionModal_Props) {
  const { t } = useTranslation();
  const mobile = useMediaQuery(`(max-width: 720px)`) || focus_view === true;
  const dispatchSuccessMode = useDispatch<StoreAppDispatch>();
  const { state: EsfState, dispatch } = useEsfContext();
  const { selectedStations, selectedLocation, error } = EsfState;

  // #region | GraphQL
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { domain, connection } = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm })
    let connection = Graph_QL.create({ domain: farm })
    return { domain, connection }
  }, [farm]);

  const { call: getDevices, data: deviceInfoState } = useDeviceInfo(connection);
  const { call: getTerminals, data: terminalsState, } = useTerminals(connection);
  const { maintainSectionLocations, deleteSectionLocations } = useOldSection(connection);

  useEffect(() => {
    getDevices({ fetchAmount: 500 }, connection);
    getTerminals({});
  }, [connection, getDevices, getTerminals])

  const terminals = useMemo(() => terminalsState[domain] && terminalsState[domain].data, [domain, terminalsState])
  const devices = useMemo(() => deviceInfoState[domain] && deviceInfoState[domain].data, [deviceInfoState, domain])
  const stations = useAppSelector((state) => selectStationByLocationId(state, location.location_id))
  // #endregion

  useEffect(() => {
    dispatch({
      selectedLocation: location,
      currentStationViewType: currentEsfViewTypes.systemControle,
      selectedStations: []
    });

    if (selectedStations && selectedStations.length > 0) {
      dispatch({ selectedStations: stations });
    }
  }, [location]);

  const [termResState, termResDispatch] = useReducer<Reducer<ITerminalResContext, TerminalResDispatcherT>>(TerminalResReducer, {
    farm: farm,
    domain: domain,
    connection: connection,
    sectionName: location.name,
    updatedLocations: []
  } as ITerminalResContext);

  // #region | Station Data 
  const stationData: TerminalDataProps[] = useMemo(() => {
    if (!terminals) return [];

    return Array.from(stations ?? []).map(currentStation => {
      const stationTerminals = currentStation.terminals ?? [];
      const currentStationTerminals = terminals?.filter(terminal => stationTerminals.includes(terminal.terminal_id)) ?? [];
      const terminalres = terminalsData({ terminals: currentStationTerminals, devices, t });

      return {
        locationName: { text: t("esfWalkThroughPages.sections.station", { defaultValue: "Station" }), value: currentStation.name ?? "" },
        terminals: terminalres,
        visual: currentStation.visual,
        location_id: currentStation.station_id
      };
    });
  }, [stations, terminals, devices]);
  // #endregion

  const handleTimeout = () => {
    setTimeout(() => {
      dispatch({ error: { errorText: "" } });
    }, 2000)
  }

  // #region | Station View
  const stationView = useMemo(() => {
    if (!selectedStations) return null;

    return selectedStations.map((station, index) => {
      if (!station) return null;

      const data = stationData.find(s => s.location_id === station.station_id);
      if (!data) return null;

      return (
        <EditTerminalsCard
          key={index}
          data={data}
          mobile={mobile}
          selected={station}
          systemType={SystemTypes.freedaEsf_WalkThrough}
        />
      );
    });
  }, [selectedStations, stationData, mobile]);
  // #endregion

  // #region | Update Data
  const updateData = async () => {
    const sectionNames = Array.from(Object.values(termResState.sectionNames ?? []));
    const penNames = Array.from(Object.values(termResState.penNames ?? []));
    const stationNames = Array.from(Object.values(termResState.stationNames ?? []));
    const terminalChanges = Array.from(Object.values(termResState.terminalValues ?? []))

    if (Object.values(penNames).some(s => !s.new_name?.trim())) {
      dispatch({
        error: {
          errorText: t("esfWalkThroughPages.newSection.penNameMissing", {
            defaultValue: "pen name missing"
          })
        }
      });
      handleTimeout();
      return;
    }

    if (penNames !== undefined || sectionNames !== undefined || terminalChanges.length) {
      const response = await maintainSectionLocations(sectionNames, penNames, stationNames, terminalChanges)

      if (!response.success) {
        dispatch({ error: { errorText: t("editFailed", { defaultValue: "edit failed" }) } })
        handleTimeout();
        return;
      }
      dispatchSuccessMode(showSuccessWithTimeout(`${sectionNames.length > 0 ? String(termResState.penNames?.[location.location_id].new_name) : location.name} ${t("wasUpdated", { defaultValue: "was updated" })}`));
      dispatch({ selectedLocation: undefined, selectedStations: [] })
      termResDispatch({ updatedLocations: [] })
      closeModal();
    }
  };
  // #endregion

  return (<TerminalResContext.Provider value={{ state: termResState, dispatch: termResDispatch }}>
    <EditSectionModal
      systemType={SystemTypes.freedaEsf_WalkThrough}
      focus_view={focus_view}
      onClose={() => {
        dispatch({ selectedLocation: undefined, selectedStations: [] })
        termResDispatch({ updatedLocations: [] })
        closeModal();
      }}
      subHeader={location.name}
      inputArea={
        <NewInput
          key={`name_${location.location_id}`}
          header={t("esfWalkThroughPages.newSection.penName", { defaultValue: "pen name" })}
          onChange={(e) => { termResDispatch({ sectionNames: [{ section_id: section.section_id, new_name: String(e) }], penNames: [{ location_id: location.location_id, new_name: String(e) }] }) }}
          isRequired
          valueOverrides={{
            defaultValue: location.name
          }}
        />
      }
      selectArea={selectedLocation &&
        <ESF_STATIONLIST
          key={selectedLocation.location_id}
          location={selectedLocation}
          popoverContent={(index) => {
            const station = stationData[index];
            return station && <LocationPopper key={station.location_id} locationData={[station]} />;
          }}
          overrides={(index) => {
            const stationId = stations[index]?.station_id;

            return {
              primaryColor: colors.greyLightSecondary,
              fittedWidth: true,
              attention: termResState.updatedLocations?.[stationId] ? 5 : undefined,
              key: stationId
            }
          }}
        />
      }
      activeArea={stationView}
      onDeleteConfirm={
        async () => {
          const response = await deleteSectionLocations(section.section_id)

          if (!response) {
            dispatch({ error: { errorText: t("buttons.deleteFailed", { defaultValue: "delete failed" }) } })
            handleTimeout();
            return;
          }
          dispatchSuccessMode(showSuccessWithTimeout(`${location.name} ${t("wasDeleted", { defaultValue: "was deleted" })}`));
          closeModal();
        }
      }
      onSave={() => updateData()}
      footerMessage={error.errorText}
    />
  </TerminalResContext.Provider>);
}
